import React, { useState } from "react";

import classNames from "classnames";
import { useTranslation } from "react-i18next";

import AdParamsButton from "./AdParamsButton";
import SwitchOnOff from "../common/tools/SwitchOnOff";
import RadioSelect from "../utils/RadioSelect";

import type { ChangeEvent, JSX } from "react";

import type { IAdvertisingSingle, VASTFormChange } from "../../types";

interface VASTParametersProps {
  onChange: VASTFormChange;
  campaign: IAdvertisingSingle;
}

function VASTParameters({
  onChange,
  campaign,
}: VASTParametersProps): JSX.Element {
  const { t } = useTranslation("editor-advertisings");
  const [isURLValid, setIsURLValid] = useState(true);

  const {
    offset_percentage: offsetPercentage,
    url,
    skip_video: skipVideo,
    skip_video_duration: skipVideoDuration,
  } = campaign;

  const handleOffsetChange = (offset: number): void => {
    onChange({
      key: "offset_percentage",
      value: offset,
    });
  };

  const handleSkipVideoChange = (isSkipVideo: boolean): void => {
    onChange({
      key: "skip_video",
      value: isSkipVideo,
    });
  };

  const validateURL = (urlToCheck: string): void => {
    const regexpURL =
      /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;

    setIsURLValid(regexpURL.test(urlToCheck));
  };

  const handleInputChange = (DOMEvent: ChangeEvent<HTMLInputElement>): void => {
    const { id, value } = DOMEvent.target;

    if (id === "url") {
      validateURL(value);
    }

    onChange({
      key: id as keyof IAdvertisingSingle,
      value,
    });
  };

  const inputUrlClassNames = classNames(
    "px-[0.625rem] py-[0.3125rem] border rounded-[0.234375rem] border-border-strong",
    {
      "border-destructive": !isURLValid,
    },
  );

  const inputDurationClassNames = classNames(
    "px-[0.625rem] py-[0.3125rem] border rounded-[0.234375rem] border-border-strong",
    {
      "border-destructive": !isURLValid,
    },
  );

  const labelClassNames = classNames(
    "text-[14px] text-muted-foreground italic",
  );

  return (
    <div className="flex flex-col gap-[0.78125rem] pt-[0.78125rem] pb-[2.5rem]">
      <div className="text-foreground text-[0.9375rem] font-medium">
        {t("vastParams")}
      </div>
      <div className="flex flex-col gap-[1.5625rem]">
        <div className="flex w-full flex-col gap-[0.46875rem]">
          <div className={labelClassNames}>URL</div>
          <input
            className={inputUrlClassNames}
            id="url"
            onChange={handleInputChange}
            type="text"
            value={url ?? ""}
          />
          <AdParamsButton url={url} />
        </div>
        <div className="flex w-full flex-col">
          <div className={labelClassNames}>{t("insertion_mode")}</div>
          <RadioSelect
            onChange={handleOffsetChange}
            selected={offsetPercentage}
            options={[
              {
                key: 0,
                value: t("pre"),
              },
              {
                key: 50,
                value: t("mid"),
              },
              {
                key: 100,
                value: t("post"),
              },
            ]}
          />
        </div>
        <div className="flex flex-col gap-[0.46875rem]">
          <div className="flex items-center gap-[0.78125rem]">
            <SwitchOnOff
              checked={skipVideo}
              iconName=""
              id="skip_video"
              name="skip_video"
              onChange={handleSkipVideoChange}
              size="sm"
            />
            <div className={labelClassNames}>{t("allowSkip")}</div>
          </div>

          {skipVideo ? (
            <div className="flex items-center gap-[0.78125rem]">
              <div className={labelClassNames}>{t("skipAfter")}</div>
              <div className="editor-form-input-suffix-s">
                <input
                  className={inputDurationClassNames}
                  id="skip_video_duration"
                  min={1}
                  onChange={handleInputChange}
                  type="number"
                  value={skipVideoDuration}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default VASTParameters;
